import { NotificationsResponse } from "@/schema"
import { useMutation, useQueryClient, type InfiniteData } from "@tanstack/react-query"

import type { ApiContext } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

import { markAllNotificationsAsRead, markNotificationAsRead, markNotificationAsUnread } from "./api"

type NotificationMutationContext = {
  previousNotifications?: InfiniteData<NotificationsResponse>
  previousUnreadCount?: number
}

type NotificationMutationFn = (context: ApiContext, id: string) => Promise<{ success: boolean; unread_count: number }>

function useNotificationReadMutation(targetRead: boolean, mutationFn: NotificationMutationFn) {
  const getContext = useApiContext()
  const queryClient = useQueryClient()
  const bookId = useBookId()

  const NOTIFICATIONS_INFINITE_KEY = ["notifications", bookId, "infinite"] as const
  const NOTIFICATIONS_UNREAD_COUNT_KEY = ["notifications", bookId, "unread_count"] as const

  return useMutation({
    mutationFn: async (id: string) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return mutationFn(context, id)
    },
    onMutate: async (id: string) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: NOTIFICATIONS_INFINITE_KEY }),
        queryClient.cancelQueries({ queryKey: NOTIFICATIONS_UNREAD_COUNT_KEY }),
      ])

      const previousNotifications =
        queryClient.getQueryData<InfiniteData<NotificationsResponse>>(NOTIFICATIONS_INFINITE_KEY)
      const previousUnreadCount = queryClient.getQueryData<number>(NOTIFICATIONS_UNREAD_COUNT_KEY)

      let previousRead: boolean | undefined

      if (previousNotifications) {
        for (const page of previousNotifications.pages) {
          const match = page.notifications.find((notification) => notification.id === id)
          if (match) {
            previousRead = !!match.read
            break
          }
        }
      }

      const readAt = targetRead ? new Date().toISOString() : null

      queryClient.setQueryData<InfiniteData<NotificationsResponse> | undefined>(NOTIFICATIONS_INFINITE_KEY, (data) => {
        if (!data) return data
        return {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            notifications: page.notifications.map((notification) =>
              notification.id === id ? { ...notification, read: targetRead, read_at: readAt } : notification
            ),
          })),
        }
      })

      if (typeof previousUnreadCount === "number" && typeof previousRead === "boolean") {
        let delta = 0
        if (targetRead && !previousRead) {
          delta = -1
        } else if (!targetRead && previousRead) {
          delta = 1
        }

        if (delta !== 0) {
          const nextCount = Math.max(0, previousUnreadCount + delta)
          queryClient.setQueryData<number | undefined>(NOTIFICATIONS_UNREAD_COUNT_KEY, nextCount)
        }
      }

      const context: NotificationMutationContext = {
        previousNotifications,
        previousUnreadCount,
      }

      return context
    },
    onError: (_error, _id, context) => {
      if (!context) return
      if (context.previousNotifications) {
        queryClient.setQueryData(NOTIFICATIONS_INFINITE_KEY, context.previousNotifications)
      }
      if (typeof context.previousUnreadCount === "number") {
        queryClient.setQueryData(NOTIFICATIONS_UNREAD_COUNT_KEY, context.previousUnreadCount)
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData<number | undefined>(NOTIFICATIONS_UNREAD_COUNT_KEY, data.unread_count)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
    },
  })
}

export function useMarkNotificationAsRead() {
  return useNotificationReadMutation(true, markNotificationAsRead)
}

export function useMarkNotificationAsUnread() {
  return useNotificationReadMutation(false, markNotificationAsUnread)
}

export function useMarkAllNotificationsAsRead() {
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return markAllNotificationsAsRead(context)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
    },
  })
}
